import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const fadeIn = (visible: boolean, delay: string) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'all 700ms ease-out',
  transitionDelay: delay,
});

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const [ref, visible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  return (
    <div ref={ref} className="text-center mb-12 md:mb-16">
      <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3" style={fadeIn(visible, '0ms')}>
        {label}
      </span>
      <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50" style={fadeIn(visible, '100ms')}>
        {title}
      </h2>
      {subtitle && <p className="text-foreground-400 text-base font-body mt-4 max-w-2xl mx-auto" style={fadeIn(visible, '200ms')}>{subtitle}</p>}
    </div>
  );
}

function AnimatedCard({ children, delay, className }: { children: React.ReactNode; delay: number; className?: string }) {
  const [ref, visible] = useScrollReveal<HTMLDivElement>({ threshold: 0.08 });
  return (
    <div ref={ref} className={className} style={fadeIn(visible, `${delay}ms`)}>
      {children}
    </div>
  );
}

export default function InvestmentPage() {
  const [heroRef, heroVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const [metricsRef, metricsVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.12 });
  const [moatTextRef, moatTextVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.12 });
  const [moatImgRef, moatImgVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.12 });
  const [timelineRef, timelineVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const [contactRef, contactVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <div className="bg-background-50/95">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-800/10 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-radial from-primary-600/8 to-transparent rounded-full blur-3xl" />
        <div ref={heroRef} className="relative max-w-7xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4" style={fadeIn(heroVisible, '0ms')}>
            / Investor Relations
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold text-foreground-50 leading-tight mb-6 max-w-4xl mx-auto" style={fadeIn(heroVisible, '100ms')}>
            Building the{' '}
            <span className="text-primary-400">Financial Backbone</span>
            {' '}of the MEA Region
          </h1>
          <p className="text-foreground-300 text-lg md:text-xl leading-relaxed mb-8 font-body max-w-3xl mx-auto" style={fadeIn(heroVisible, '200ms')}>
            TMT is redefining digital payments across the Middle East, Africa, and Europe —
            bridging 14+ corridors with a fully regulated, AI-powered e-wallet ecosystem trusted
            by thousands of users and businesses.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={fadeIn(heroVisible, '350ms')}>
            <a
              href="#investor-contact"
              className="px-8 py-4 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Inquire About Investment
              <i className="ri-arrow-right-line ml-2" />
            </a>
            <a
              href="#market-opportunity"
              className="px-8 py-4 border border-primary-600/50 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              Explore the Opportunity
              <i className="ri-arrow-down-line ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Key Metrics Banner */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-background-100/95 border-y border-primary-800/20">
        <div ref={metricsRef} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: '$48M+', label: 'Annual Transaction Volume', icon: 'ri-funds-line' },
              { value: '14+', label: 'Market Corridors Active', icon: 'ri-global-line' },
              { value: '250K+', label: 'Active Users & Growing', icon: 'ri-user-3-line' },
              { value: '98.7%', label: 'Transaction Success Rate', icon: 'ri-check-double-line' },
            ].map((metric, i) => (
              <div key={metric.label} className="text-center" style={fadeIn(metricsVisible, `${i * 100}ms`)}>
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-600/20 mb-3">
                  <i className={`${metric.icon} text-primary-400 text-xl`} />
                </div>
                <p className="font-heading text-3xl md:text-4xl font-bold text-primary-400 mb-1">{metric.value}</p>
                <p className="text-foreground-400 text-sm font-body">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section id="market-opportunity" className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <SectionHeader label="/ Market Opportunity" title="A Massive, Underserved Market" subtitle="The Middle East and Africa represent one of the fastest-growing fintech regions globally, with remittance flows exceeding $100 billion annually and a vast unbanked population hungry for digital solutions." />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-bank-line', title: '$100B+ Annual Remittances', desc: 'The MEA corridor processes over $100 billion in remittances each year. Traditional channels charge 6–10% in fees — TMT delivers the same transfers at a fraction of the cost, capturing margin while passing savings to customers.' },
              { icon: 'ri-smartphone-line', title: '60% Smartphone Penetration & Rising', desc: 'With smartphone adoption accelerating across Egypt, Saudi Arabia, and the Gulf, the addressable market for mobile-first financial services expands by millions of new users every quarter.' },
              { icon: 'ri-user-unfollow-line', title: '350M+ Unbanked Adults', desc: 'Over 350 million adults across Africa and the Middle East lack access to traditional banking. TMT gives them a bank in their pocket — no branch visit required.' },
              { icon: 'ri-government-line', title: 'Regulatory Tailwinds', desc: 'Central banks across Egypt, UAE, and Saudi Arabia are actively promoting cashless economies and digital payment infrastructure. TMT operates in full compliance, positioning us as a preferred partner for government-led financial inclusion initiatives.' },
              { icon: 'ri-line-chart-line', title: '25% YoY Fintech Growth', desc: 'MEA fintech revenue is projected to grow at 25% CAGR through 2030. As one of the few regulated multi-corridor players, TMT is uniquely positioned to capture outsized market share.' },
              { icon: 'ri-exchange-dollar-line', title: 'B2B & Payroll Opportunity', desc: 'Beyond consumer remittances, TMT addresses the $50B+ cross-border payroll and B2B payment market — a high-margin segment with sticky enterprise relationships and recurring revenue.' },
            ].map((item, i) => (
              <AnimatedCard key={item.title} delay={i * 100} className="p-6 md:p-8 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500 group">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-600/20 mb-5 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${item.icon} text-primary-400 text-xl`} />
                </div>
                <h3 className="font-heading text-lg text-foreground-100 mb-3">{item.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{item.desc}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div ref={moatTextRef} className="w-full lg:w-1/2">
              <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3" style={fadeIn(moatTextVisible, '0ms')}>
                / Our Moat
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-6" style={fadeIn(moatTextVisible, '100ms')}>
                Why TMT Wins
              </h2>
              <div className="space-y-5">
                {[
                  { title: 'Full Regulatory Compliance', desc: 'Licensed and operating under central banking regulations across all active corridors. Compliance is not an afterthought — it is our foundation, giving partners and investors confidence in long-term sustainability.' },
                  { title: 'Multi-Corridor Architecture', desc: 'Unlike single-market competitors, TMT was architected from day one to operate across borders. Our platform handles multi-currency settlement, regulatory variance, and corridor-specific compliance seamlessly.' },
                  { title: 'AI-Powered Risk & Fraud Engine', desc: 'Proprietary machine learning models monitor every transaction in real time, achieving a 98.7% success rate with industry-leading fraud detection precision that keeps costs low and trust high.' },
                  { title: 'Physical + Digital Hybrid Model', desc: 'Our network of physical agent locations combined with a fully digital app creates an omnichannel experience. Customers get the trust of in-person service with the convenience of digital — a model competitors cannot easily replicate.' },
                  { title: 'Sticky Ecosystem', desc: 'Once a user joins TMT for money transfer, they stay for bill payments, digital cards, payroll, and scan-to-pay. Our average revenue per user grows 3.2x within 12 months of acquisition.' },
                ].map((advantage, i) => (
                  <div key={i} className="flex gap-4" style={fadeIn(moatTextVisible, `${200 + i * 120}ms`)}>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0 mt-0.5">
                      <i className="ri-shield-star-line text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base text-foreground-100 mb-1">{advantage.title}</h3>
                      <p className="text-foreground-400 text-sm leading-relaxed font-body">{advantage.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div ref={moatImgRef} className="w-full lg:w-1/2">
              <div className="relative overflow-hidden rounded-2xl border border-primary-700/20" style={fadeIn(moatImgVisible, '150ms')}>
                <div className="absolute inset-0 bg-gradient-to-tr from-background-50/30 via-transparent to-transparent z-10" />
                <img
                  src="https://readdy.ai/api/search-image?query=Futuristic%20fintech%20control%20room%20with%20multiple%20holographic%20screens%20displaying%20global%20financial%20data%20flows%20golden%20and%20warm%20accent%20lighting%20sleek%20modern%20architecture%20sophisticated%20technology%20operations%20center%20Middle%20East%20headquarters%20professional%20atmosphere%20with%20data%20visualization%20dashboards&width=1000&height=1400&seq=investment-moat-01&orientation=portrait"
                  alt="TMT Technology Infrastructure — AI-Powered Financial Operations Center"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Model */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <SectionHeader label="/ Business Model" title="Diversified, Recurring Revenue" subtitle="Multiple revenue streams create resilience and compound growth — our model gets stronger with every new user and corridor." />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[
              { icon: 'ri-arrow-left-right-line', title: 'Transaction Fees', percentage: '~45%', desc: 'Per-transaction fees on money transfers, bill payments, and cross-border settlements. Margin improves with volume as fixed infrastructure costs are already absorbed.' },
              { icon: 'ri-exchange-funds-line', title: 'FX Spread Revenue', percentage: '~25%', desc: 'Competitive foreign exchange spreads on multi-currency transactions across 14+ corridors. As corridor volume grows, FX revenue scales with minimal incremental cost.' },
              { icon: 'ri-bank-card-line', title: 'Card Issuance & Interchange', percentage: '~15%', desc: 'Revenue from digital and physical Visa card issuance, annual fees, and interchange on every card-present and card-not-present transaction.' },
              { icon: 'ri-building-4-line', title: 'Enterprise & B2B Services', percentage: '~15%', desc: 'Recurring SaaS-style revenue from payroll disbursement, merchant payment gateways, and API access for enterprise partners integrating TMT into their workflows.' },
            ].map((stream, i) => (
              <AnimatedCard key={stream.title} delay={i * 120} className="p-6 md:p-8 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-600/20">
                    <i className={`${stream.icon} text-primary-400 text-xl`} />
                  </div>
                  <span className="font-heading text-2xl font-bold text-primary-400">{stream.percentage}</span>
                </div>
                <h3 className="font-heading text-lg text-foreground-100 mb-2">{stream.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{stream.desc}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Traction & Milestones */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <SectionHeader label="/ Traction" title="Milestones & Momentum" />

          <div ref={timelineRef} className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-primary-700/30 hidden lg:block" />

            <div className="space-y-8 lg:space-y-0">
              {[
                { year: '2023', title: 'Foundation & Licensing', desc: 'Company incorporated. Secured initial regulatory approvals and began building the core e-wallet infrastructure. Assembled founding leadership team with combined 60+ years of fintech and banking experience.', side: 'left' },
                { year: '2024', title: 'Platform Launch & First Corridor', desc: 'Launched the TMT mobile application on iOS and Android. Opened the Egypt–Saudi Arabia corridor, processing $2.8M in transactions within the first quarter. Signed first 50 agent partners.', side: 'right' },
                { year: '2025', title: 'Multi-Corridor Expansion', desc: 'Expanded to 14+ corridors across the Middle East, Africa, and Europe. Launched digital Visa cards and bill payment services. Transaction volume surpassed $48M annualized with 250K+ active users.', side: 'left' },
                { year: '2026', title: 'Enterprise & Growth Phase', desc: 'Launched B2B payroll and merchant payment solutions. Rolling out physical Visa cards and expanding agent network to 500+ locations. Targeting $200M+ annual transaction volume and new corridor openings.', side: 'right' },
              ].map((milestone, i) => (
                <div key={i} className={`relative flex flex-col lg:flex-row items-center gap-6 lg:gap-0 ${milestone.side === 'right' ? 'lg:flex-row-reverse' : ''}`} style={fadeIn(timelineVisible, `${i * 200}ms`)}>
                  <div className={`w-full lg:w-1/2 ${milestone.side === 'left' ? 'lg:pr-16 lg:text-right' : 'lg:pl-16'}`}>
                    <div className="bg-background-50/90 border border-primary-800/20 rounded-xl p-6 hover:border-primary-600/40 transition-all duration-500">
                      <span className="inline-block font-heading text-2xl font-bold text-primary-400 mb-2">{milestone.year}</span>
                      <h3 className="font-heading text-lg text-foreground-100 mb-2">{milestone.title}</h3>
                      <p className="text-foreground-400 text-sm leading-relaxed font-body">{milestone.desc}</p>
                    </div>
                  </div>
                  {/* Timeline dot */}
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-100 z-10" />
                  <div className="w-full lg:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use of Funds */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <SectionHeader label="/ Use of Funds" title="Where Your Capital Goes" subtitle="Every dollar invested fuels our expansion engine — driving growth across corridors, technology, and talent." />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {[
              { icon: 'ri-global-line', title: 'Corridor Expansion', percentage: '35%', desc: 'Opening new payment corridors, securing additional regulatory licenses, and establishing local banking partnerships across Africa and Europe.' },
              { icon: 'ri-code-s-slash-line', title: 'Technology & AI', percentage: '25%', desc: 'Advancing our AI fraud detection engine, building new product verticals, and scaling platform infrastructure for 10x transaction volume growth.' },
              { icon: 'ri-team-line', title: 'Talent Acquisition', percentage: '20%', desc: 'Hiring top engineering, compliance, and business development talent across Cairo, Dubai, and London offices to accelerate execution.' },
              { icon: 'ri-megaphone-line', title: 'Marketing & User Growth', percentage: '20%', desc: 'Scaling customer acquisition across existing and new markets through digital campaigns, agent partnerships, and community-driven growth.' },
            ].map((fund, i) => (
              <AnimatedCard key={fund.title} delay={i * 120} className="p-6 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-600/20">
                    <i className={`${fund.icon} text-primary-400 text-xl`} />
                  </div>
                  <span className="font-heading text-3xl font-bold text-primary-400">{fund.percentage}</span>
                </div>
                <h3 className="font-heading text-lg text-foreground-100 mb-2">{fund.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{fund.desc}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Investor Contact CTA */}
      <section id="investor-contact" className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95 border-t border-primary-700/20">
        <div ref={contactRef} className="max-w-3xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4" style={fadeIn(contactVisible, '0ms')}>
            / Get In Touch
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 mb-4" style={fadeIn(contactVisible, '100ms')}>
            Partner With Us
          </h2>
          <p className="text-foreground-300 text-base md:text-lg leading-relaxed mb-8 font-body max-w-xl mx-auto" style={fadeIn(contactVisible, '200ms')}>
            We are actively engaging with strategic and institutional investors who share our
            vision of building the definitive financial platform for the MEA region. For
            investment inquiries, detailed financials, and partnership discussions, reach out
            to our investor relations team.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={fadeIn(contactVisible, '350ms')}>
            <Link
              to="/contact"
              className="px-8 py-4 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Contact Investor Relations
              <i className="ri-arrow-right-line ml-2" />
            </Link>
            <a
              href="mailto:investor@trustedmoneytransfer.com"
              className="px-8 py-4 border border-primary-600/50 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              investor@trustedmoneytransfer.com
              <i className="ri-mail-line ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-foreground-600 text-xs leading-relaxed font-body text-center">
            This page contains forward-looking statements and projections based on current
            expectations and assumptions. Actual results may differ materially. This
            communication does not constitute an offer to sell or solicitation of an offer
            to buy securities. Investment opportunities are available only to qualified
            investors under applicable securities laws. Past performance and projections
            are not guarantees of future results.
          </p>
        </div>
      </section>
    </div>
  );
}